import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import type { EditableSectionSplit, LayoutBlockMeta } from './pageDelimitationLayout';

import { usePageDelimitationScale } from '../contexts/page-delimitation-scale.hooks';
import {
  applyChunkSplit,
  buildBlockMetas,
  computePageCountFromBottom,
  createInitialListSplitsResult,
  splitChunkByAvailableSpace,
  type ListSplitsResult,
} from './pageDelimitationLayout';
import { waitForRenderAssets } from './renderReady';
import { useContainerStable } from './useContainerStable';
import { useListSplittables } from './useListSplittables';
import { useMeasureBlocks } from './useMeasureBlocks';

export type { EditableSectionSplit };
export type { SectionRefsRef } from './useMeasureBlocks';

const DEFAULT_PAGE_BOTTOM_LOGO_PX = 64;
const TOLERANCE_PX = 13;
const STABLE_FRAME_COUNT = 3;

interface RenderedBlockSnapshot {
  top: number;
  bottom: number;
  height: number;
}

export interface UsePageDelimitationOptions {
  contentKey?: string | number;
  gapPx?: number;
  bodyBottomPaddingPx?: number;
  splitableSections?: [number, number][];
  sectionCountPerColumn?: number[];
  pageBottomLogoPx?: number;
  /** Top padding applied to pages after the first one. Defaults to gapPx if not set. */
  pageTopPaddingPx?: number;
}

export interface UsePageDelimitationResult {
  blockOffsets: number[][];
  splitLayout: (EditableSectionSplit[] | null)[][];
  pageCount: number;
  pageHeightPx: number;
  isLayoutReady: boolean;
}

const EMPTY_LAYOUT: ListSplitsResult = {
  blockOffsets: [],
  splitLayout: [],
  pageCount: 0,
  pageHeightPx: 0,
  safePageHeightPx: 0,
  allBlockHeights: [],
  bodyStartPxPerColumn: [],
};

function roundPx(value: number): number {
  return Math.round(value * 10) / 10;
}

function measureRenderedBlocks(
  container: HTMLDivElement,
  refs: (HTMLElement | null)[][],
  scale: number,
): { snapshotsByCol: RenderedBlockSnapshot[][]; bodyStartPxPerColumn: number[] } | null {
  const containerRect = container.getBoundingClientRect();
  const toLogical = (value: number) => (scale !== 1 ? value / scale : value);

  const snapshotsByCol: RenderedBlockSnapshot[][] = [];
  const bodyStartPxPerColumn: number[] = [];

  for (let col = 0; col < refs.length; col++) {
    const columnRefs = refs[col];
    if (!columnRefs?.length) {
      snapshotsByCol[col] = [];
      continue;
    }

    const snapshots: RenderedBlockSnapshot[] = [];
    for (let blockIndex = 0; blockIndex < columnRefs.length; blockIndex++) {
      const element = columnRefs[blockIndex];
      if (!element) {
        return null;
      }

      const rect = element.getBoundingClientRect();
      const top = toLogical(rect.top - containerRect.top);
      const height = toLogical(rect.height);
      snapshots.push({
        top,
        height,
        bottom: top + height,
      });
    }

    snapshotsByCol[col] = snapshots;
    bodyStartPxPerColumn[col] = snapshots[0]?.top ?? 0;
  }

  return { snapshotsByCol, bodyStartPxPerColumn };
}

function findFirstOverflow(
  snapshotsByCol: RenderedBlockSnapshot[][],
  blockMetasByCol: LayoutBlockMeta[][],
  pageHeightPx: number,
  safePageHeightPx: number,
): { meta: LayoutBlockMeta; snapshot: RenderedBlockSnapshot } | null {
  for (let col = 0; col < snapshotsByCol.length; col++) {
    const snapshots = snapshotsByCol[col] ?? [];
    const metas = blockMetasByCol[col] ?? [];

    for (let blockIndex = 0; blockIndex < snapshots.length; blockIndex++) {
      const snapshot = snapshots[blockIndex];
      const meta = metas[blockIndex];
      if (!snapshot || !meta) {
        continue;
      }

      const pageIndex = Math.floor(snapshot.top / pageHeightPx);
      const safeBottom = pageIndex * pageHeightPx + safePageHeightPx;
      if (snapshot.bottom > safeBottom + 0.5) {
        return { meta, snapshot };
      }
    }
  }

  return null;
}

function computePageCountFromSnapshots(
  snapshotsByCol: RenderedBlockSnapshot[][],
  bodyBottomPaddingPx: number,
  pageHeightPx: number,
): number {
  const maxBottomPx = snapshotsByCol.reduce((maxBottom, column) => {
    const lastBottom = column[column.length - 1]?.bottom ?? 0;
    return Math.max(maxBottom, lastBottom + bodyBottomPaddingPx);
  }, 0);

  return computePageCountFromBottom(maxBottomPx, pageHeightPx);
}

function replaceColumnOffsetsFromBlock(
  currentOffsets: number[],
  startIndex: number,
  nextLength: number,
  firstOffset: number,
): number[] {
  const preservedBefore = currentOffsets.slice(0, startIndex);
  const preservedAfter = Array.from({ length: Math.max(0, nextLength - startIndex - 1) }, () => 0);
  return [...preservedBefore, firstOffset, ...preservedAfter];
}

function buildLayoutSignature(layout: ListSplitsResult): string {
  return JSON.stringify({
    splitLayout: layout.splitLayout,
    blockOffsets: layout.blockOffsets.map(column => column.map(roundPx)),
    pageCount: layout.pageCount,
    allBlockHeights: layout.allBlockHeights.map(column => column.map(roundPx)),
    bodyStartPxPerColumn: layout.bodyStartPxPerColumn.map(roundPx),
  });
}

function buildStableSignature(
  snapshotsByCol: RenderedBlockSnapshot[][],
  pageCount: number,
): string {
  return JSON.stringify({
    pageCount,
    snapshotsByCol: snapshotsByCol.map(column =>
      column.map(snapshot => ({
        top: roundPx(snapshot.top),
        height: roundPx(snapshot.height),
      })),
    ),
  });
}

function checkFirstBlockOnPageMargins(
  blockOffsets: number[][],
  allBlockHeights: number[][],
  bodyStartPxPerColumn: number[],
  pageHeightPx: number,
  gapPx: number,
  pageTopPaddingPx: number,
): void {
  for (let col = 0; col < blockOffsets.length; col++) {
    const offsets = blockOffsets[col];
    const heights = allBlockHeights[col];
    if (!offsets?.length || !heights?.length || offsets.length !== heights.length) {
      continue;
    }

    let currentTop = bodyStartPxPerColumn[col] ?? 0;
    for (let blockIndex = 0; blockIndex < offsets.length; blockIndex++) {
      const marginTop = offsets[blockIndex] ?? 0;
      const blockTop = currentTop + marginTop;
      const pageIndex = Math.floor(blockTop / pageHeightPx);
      const pageTopPadding = pageIndex > 0 ? pageTopPaddingPx : gapPx;
      const expectedTop = pageIndex * pageHeightPx + pageTopPadding;

      if (marginTop > 0 && Math.abs(blockTop - expectedTop) > TOLERANCE_PX) {
        console.debug(
          '[PageDelim] First block on page has wrong top margin',
          JSON.stringify({
            col,
            blockIndex,
            pageIndex,
            expectedTop: roundPx(expectedTop),
            actualTop: roundPx(blockTop),
            marginTop: roundPx(marginTop),
          }),
        );
      }

      currentTop =
        blockTop + (heights[blockIndex] ?? 0) + (blockIndex < offsets.length - 1 ? gapPx : 0);
    }
  }
}

export function usePageDelimitation(
  sectionRefsRef: React.RefObject<(HTMLElement | null)[][]>,
  containerRef: React.RefObject<HTMLDivElement | null>,
  options: UsePageDelimitationOptions = {},
): UsePageDelimitationResult {
  const {
    contentKey,
    gapPx = 8,
    bodyBottomPaddingPx = 24,
    splitableSections = [],
    sectionCountPerColumn = [],
    pageBottomLogoPx = DEFAULT_PAGE_BOTTOM_LOGO_PX,
    pageTopPaddingPx,
  } = options;

  const effectivePageTopPaddingPx = pageTopPaddingPx ?? 48;

  const scale = usePageDelimitationScale();
  const [areRenderAssetsReady, setAreRenderAssetsReady] = useState(false);
  const [layoutState, setLayoutState] = useState<ListSplitsResult>(EMPTY_LAYOUT);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAreRenderAssetsReady(false);

    const markReady = async () => {
      await waitForRenderAssets();
      if (!cancelled) {
        setAreRenderAssetsReady(true);
      }
    };

    void markReady();

    return () => {
      cancelled = true;
    };
  }, [contentKey, scale]);

  const isStable = useContainerStable(containerRef, [
    areRenderAssetsReady,
    contentKey,
    gapPx,
    bodyBottomPaddingPx,
    splitableSections,
    sectionCountPerColumn,
  ]);

  const canMeasure = areRenderAssetsReady && isStable;

  const blocks = useMeasureBlocks(
    containerRef,
    sectionRefsRef,
    { sectionCountPerColumn, contentKey, scale },
    canMeasure,
  );

  const splitableByKey = useListSplittables(
    sectionRefsRef,
    { gapPx, splitableSections, sectionCountPerColumn, contentKey, scale },
    canMeasure,
  );

  const measurement = useMemo(() => {
    if (!blocks) {
      return null;
    }

    return {
      ...blocks,
      splitableByKey: splitableByKey ?? new Map(),
    };
  }, [blocks, splitableByKey]);

  useLayoutEffect(() => {
    if (!measurement) {
      setLayoutState(EMPTY_LAYOUT);
      setIsLayoutReady(false);
      return;
    }

    setLayoutState(
      createInitialListSplitsResult(measurement, {
        gapPx,
        bodyBottomPaddingPx,
        splitableSections,
        pageBottomLogoPx,
        pageTopPaddingPx: effectivePageTopPaddingPx,
      }),
    );
    setIsLayoutReady(false);
  }, [
    measurement,
    gapPx,
    bodyBottomPaddingPx,
    splitableSections,
    pageBottomLogoPx,
    effectivePageTopPaddingPx,
  ]);

  useLayoutEffect(() => {
    if (!measurement || layoutState.pageCount === 0) {
      setIsLayoutReady(false);
      return;
    }

    const container = containerRef.current;
    const refs = sectionRefsRef.current;
    if (!container || !refs?.length) {
      setIsLayoutReady(false);
      return;
    }

    const blockMetasByCol = buildBlockMetas(measurement, layoutState.splitLayout);
    const expectedCountsMatch = blockMetasByCol.every(
      (column, col) =>
        (refs[col]?.length ?? 0) === column.length && refs[col]?.every(ref => ref !== null),
    );
    if (!expectedCountsMatch) {
      setIsLayoutReady(false);
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let stableFrames = 0;
    let lastStableSignature: string | null = null;

    const run = () => {
      if (cancelled) {
        return;
      }

      const rendered = measureRenderedBlocks(container, refs, scale);
      if (!rendered) {
        rafId = requestAnimationFrame(run);
        return;
      }

      const { snapshotsByCol, bodyStartPxPerColumn } = rendered;
      const allBlockHeights = snapshotsByCol.map(column => column.map(snapshot => snapshot.height));
      const nextPageCount = computePageCountFromSnapshots(
        snapshotsByCol,
        bodyBottomPaddingPx,
        layoutState.pageHeightPx,
      );
      const overflow = findFirstOverflow(
        snapshotsByCol,
        blockMetasByCol,
        layoutState.pageHeightPx,
        layoutState.safePageHeightPx,
      );

      if (overflow) {
        setIsLayoutReady(false);
        stableFrames = 0;
        lastStableSignature = null;

        const { meta, snapshot } = overflow;
        const currentPageIndex = Math.floor(snapshot.top / layoutState.pageHeightPx);
        const pageTopPaddingForCurrentPage =
          currentPageIndex > 0 ? effectivePageTopPaddingPx : gapPx;
        const expectedTop =
          currentPageIndex * layoutState.pageHeightPx + pageTopPaddingForCurrentPage;
        const isAlreadyPushedToPageTop = Math.abs(snapshot.top - expectedTop) <= TOLERANCE_PX;
        const availableHeightPx =
          currentPageIndex * layoutState.pageHeightPx + layoutState.safePageHeightPx - snapshot.top;

        if (meta.isSplitable) {
          const splitable = measurement.splitableByKey.get(meta.splitableKey);
          if (splitable) {
            const nextSplit = splitChunkByAvailableSpace(splitable, meta.chunk, availableHeightPx);

            if (nextSplit) {
              const nextBlockMetasByCol = buildBlockMetas(
                measurement,
                applyChunkSplit(measurement, layoutState.splitLayout, meta, nextSplit),
              );
              const nextSplitLayout = applyChunkSplit(
                measurement,
                layoutState.splitLayout,
                meta,
                nextSplit,
              );
              const nextOffsets = layoutState.blockOffsets.map((column, col) => {
                if (col !== meta.col) {
                  return [...column];
                }

                const nextLength = nextBlockMetasByCol[col]?.length ?? column.length;
                return replaceColumnOffsetsFromBlock(
                  column,
                  meta.blockIndex,
                  nextLength,
                  column[meta.blockIndex] ?? 0,
                );
              });
              const nextHeights = nextOffsets.map((column, col) =>
                Array.from(
                  { length: column.length },
                  (_, blockIndex) => allBlockHeights[col]?.[blockIndex] ?? 0,
                ),
              );

              const nextLayout: ListSplitsResult = {
                ...layoutState,
                splitLayout: nextSplitLayout,
                blockOffsets: nextOffsets,
                allBlockHeights: nextHeights,
                bodyStartPxPerColumn,
                pageCount: Math.max(layoutState.pageCount, nextPageCount),
              };

              if (buildLayoutSignature(nextLayout) !== buildLayoutSignature(layoutState)) {
                setLayoutState(nextLayout);
                return;
              }
            }
          }
        }

        if (!isAlreadyPushedToPageTop) {
          const nextOffsets = layoutState.blockOffsets.map(column => [...column]);
          const targetPageIndex = currentPageIndex + 1;
          const pageTopPaddingForTargetPage =
            targetPageIndex > 0 ? effectivePageTopPaddingPx : gapPx;
          const targetTop =
            targetPageIndex * layoutState.pageHeightPx + pageTopPaddingForTargetPage;
          const extraOffset = targetTop - snapshot.top;
          nextOffsets[meta.col]![meta.blockIndex] =
            (nextOffsets[meta.col]?.[meta.blockIndex] ?? 0) + Math.max(0, extraOffset);

          const nextLayout: ListSplitsResult = {
            ...layoutState,
            blockOffsets: nextOffsets,
            allBlockHeights,
            bodyStartPxPerColumn,
            pageCount: Math.max(layoutState.pageCount, nextPageCount),
          };

          if (buildLayoutSignature(nextLayout) !== buildLayoutSignature(layoutState)) {
            setLayoutState(nextLayout);
            return;
          }
        }
      }

      const normalizedLayout: ListSplitsResult = {
        ...layoutState,
        allBlockHeights,
        bodyStartPxPerColumn,
        pageCount: nextPageCount,
      };

      if (buildLayoutSignature(normalizedLayout) !== buildLayoutSignature(layoutState)) {
        setLayoutState(normalizedLayout);
        return;
      }

      const stableSignature = buildStableSignature(snapshotsByCol, nextPageCount);
      if (stableSignature === lastStableSignature) {
        stableFrames += 1;
      } else {
        stableFrames = 1;
        lastStableSignature = stableSignature;
      }

      if (stableFrames >= STABLE_FRAME_COUNT) {
        setIsLayoutReady(true);
        return;
      }

      rafId = requestAnimationFrame(run);
    };

    setIsLayoutReady(false);
    rafId = requestAnimationFrame(run);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [
    measurement,
    layoutState,
    containerRef,
    sectionRefsRef,
    scale,
    gapPx,
    bodyBottomPaddingPx,
    effectivePageTopPaddingPx,
  ]);

  return useMemo(() => {
    checkFirstBlockOnPageMargins(
      layoutState.blockOffsets,
      layoutState.allBlockHeights,
      layoutState.bodyStartPxPerColumn,
      layoutState.pageHeightPx,
      gapPx,
      effectivePageTopPaddingPx,
    );

    return {
      ...layoutState,
      isLayoutReady,
    };
  }, [layoutState, isLayoutReady, gapPx, effectivePageTopPaddingPx]);
}
